import React, { useEffect, useMemo, useRef, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import moment from "moment";
import {
  mdiChevronLeft,
  mdiChevronRight,
  mdiCloudOutline,
  mdiCurrentAc,
  mdiFire,
  mdiFlash,
  mdiLightningBolt,
  mdiLightningBoltOutline,
  mdiPineTree,
  mdiWhiteBalanceSunny,
} from "@mdi/js";
import Main from "../../components/layout/Main";
import MdiIcon from "../../components/device/app/MdiIcon";
import { useToggleTop } from "../../components/common/ToggleTop";
import { UserService } from "../../services/UserService";
import "../../assets/styles/app-energy.css";

// An energy meter reading over time, ported from the app's
// app/screens/EnergyMeterGraphScreen.js: the period tabs, date arrows, total
// and average, the chart, its legend and the environmental impact.

const PURPLE = "#8B5CF6";
const LIGHT_PURPLE = "#C4B5FD";
const GREY = "#8A8A8A";
const LIGHTGRAY = "#bdbdbd";
const WHITESMOKE = "#e3e3e3";

const PERIODS = ["Hour", "Day", "Month", "Year"];
const YEAR_WINDOW = 6;
const CHART_HEIGHT = 245;

const METRICS = {
  consumed: {
    key: "consumed_kwh",
    legendTitle: "Energy Consumed",
    yAxisLabel: "Energy (kWh)",
    totalLabel: "Total Consumed",
    averageLabel: "Average Usage",
    unit: "kWh",
    hasTotal: true,
    hasAverage: true,
    hasEnvironment: true,
    icon: mdiLightningBolt,
  },
  generated: {
    key: "generated_kwh",
    legendTitle: "Energy Generated",
    yAxisLabel: "Energy (kWh)",
    totalLabel: "Total Generated",
    averageLabel: "Average Generation",
    unit: "kWh",
    hasTotal: true,
    hasAverage: true,
    hasEnvironment: true,
    icon: mdiWhiteBalanceSunny,
  },
  voltage: {
    key: "avg_voltage",
    legendTitle: "Average Voltage",
    yAxisLabel: "Voltage (V)",
    unit: "V",
    icon: mdiFlash,
  },
  current: {
    key: "avg_current",
    legendTitle: "Average Current",
    yAxisLabel: "Current (A)",
    unit: "A",
    icon: mdiCurrentAc,
  },
  power: {
    key: "avg_power",
    legendTitle: "Average Power",
    yAxisLabel: "Power (kW)",
    unit: "kW",
    icon: mdiLightningBoltOutline,
  },
};

// Energy adds up over a period; voltage, current and power average out.
const SUMMED = ["consumed_kwh", "generated_kwh"];

// The from/to the app requests for each period.
const rangeFor = (period, date) => {
  const now = moment();
  if (period === "Hour") {
    const day = date.clone().utc();
    return {
      from: day.clone().startOf("day").toISOString(),
      to: day.isSame(now.clone().utc(), "day") ? now.toISOString() : day.clone().endOf("day").toISOString(),
    };
  }
  if (period === "Day") {
    return {
      from: date.clone().startOf("month").toISOString(),
      to: date.isSame(now, "month") ? now.toISOString() : date.clone().endOf("month").toISOString(),
    };
  }
  if (period === "Month") {
    return {
      from: date.clone().startOf("year").toISOString(),
      to: date.isSame(now, "year") ? now.toISOString() : date.clone().endOf("year").toISOString(),
    };
  }
  return {
    from: date.clone().subtract(5, "year").startOf("year").toISOString(),
    to: date.isSame(now, "year") ? now.toISOString() : date.clone().endOf("year").toISOString(),
  };
};

const parseEnergyValue = (value) => {
  if (value === null || value === undefined) return 0;
  const parsed = parseFloat(String(value).replace(/[^\d.-]/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const aggregateByKey = (series, keyFormat, metricKey) => {
  const groups = new Map();
  series.forEach((item) => {
    if (!item?.bucket_time) return;
    const key = moment(item.bucket_time).format(keyFormat);
    groups.set(key, [...(groups.get(key) || []), parseEnergyValue(item[metricKey])]);
  });
  const result = new Map();
  groups.forEach((values, key) => {
    const sum = values.reduce((total, value) => total + value, 0);
    result.set(key, SUMMED.includes(metricKey) ? sum : sum / values.length);
  });
  return result;
};

const yearsToShow = (series, referenceYear) => {
  const dataYears = series.map((item) => item?.bucket_time && moment(item.bucket_time).year()).filter(Number.isFinite);
  const maxDataYear = dataYears.length ? Math.max(...dataYears) : referenceYear;
  const minDataYear = dataYears.length ? Math.min(...dataYears) : referenceYear;
  const end = Math.min(Math.max(referenceYear, maxDataYear), moment().year());
  const start = Math.min(end - (YEAR_WINDOW - 1), minDataYear);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

// One bar per hour, day, month or year of the period, 0 where nothing was
// recorded. Hourly bucket_time values are UTC, so that day is built in UTC.
const buildPeriodBuckets = (series, period, referenceDate, metricKey) => {
  const safe = Array.isArray(series) ? series : [];
  const now = moment();
  const bucket = (time, value) => ({ bucket_time: time.toISOString(), value: value || 0 });

  if (period === "Hour") {
    const dayStart = referenceDate.clone().utc().startOf("day");
    const hours = referenceDate.clone().utc().isSame(moment().utc(), "day") ? moment().utc().hour() + 1 : 24;
    const values = new Map();
    safe.forEach((item) => {
      if (item?.bucket_time) values.set(moment.utc(item.bucket_time).format("YYYY-MM-DD-HH"), parseEnergyValue(item[metricKey]));
    });
    return Array.from({ length: hours }, (_, index) => {
      const time = dayStart.clone().add(index, "hours");
      return bucket(time, values.get(time.format("YYYY-MM-DD-HH")));
    });
  }
  if (period === "Day") {
    const sums = aggregateByKey(safe, "YYYY-MM-DD", metricKey);
    const days = referenceDate.isSame(now, "month") ? now.date() : referenceDate.daysInMonth();
    const monthStart = referenceDate.clone().startOf("month");
    return Array.from({ length: days }, (_, index) => {
      const time = monthStart.clone().add(index, "days");
      return bucket(time, sums.get(time.format("YYYY-MM-DD")));
    });
  }
  if (period === "Month") {
    const year = referenceDate.year();
    const sums = aggregateByKey(safe, "YYYY-MM", metricKey);
    const months = year === now.year() ? now.month() + 1 : 12;
    return Array.from({ length: months }, (_, index) => {
      const time = moment({ year, month: index, day: 1 });
      return bucket(time, sums.get(time.format("YYYY-MM")));
    });
  }
  const sums = aggregateByKey(safe, "YYYY", metricKey);
  return yearsToShow(safe, referenceDate.year()).map((year) => {
    const time = moment({ year, month: 0, day: 1 });
    return bucket(time, sums.get(time.format("YYYY")));
  });
};

const isFuture = (bucketTime, period) => {
  const time = moment(bucketTime);
  const now = moment();
  if (period === "Hour") return time.isAfter(now);
  return time.isAfter(now, { Day: "day", Month: "month", Year: "year" }[period]);
};

const SMALL_BUCKET_THRESHOLD = 15;

// Every label when there are few bars, otherwise the even ones and the last.
const dynamicLabelIndexes = (count, numberAt) => {
  if (count <= 0) return [];
  if (count <= SMALL_BUCKET_THRESHOLD) return Array.from({ length: count }, (_, index) => index);
  const indexes = [];
  for (let index = 0; index < count; index += 1) {
    if (numberAt(index) % 2 === 0) indexes.push(index);
  }
  if (!indexes.includes(count - 1)) indexes.push(count - 1);
  return indexes;
};

const labelIndexesFor = (bars, period) => {
  if (period === "Hour") return dynamicLabelIndexes(bars.length, (index) => index);
  if (period === "Month") return bars.map((_, index) => index);
  if (period === "Year") return dynamicLabelIndexes(bars.length, (index) => moment(bars[index].bucket_time).year());
  const indexes = dynamicLabelIndexes(bars.length, (index) => index + 1);
  // An odd last day replaces the even day before it, so the two don't collide.
  if (bars.length > SMALL_BUCKET_THRESHOLD && bars.length % 2 !== 0) {
    return indexes.filter((index) => index !== bars.length - 2).concat(bars.length - 1);
  }
  return indexes;
};

const AXIS_FORMAT = { Hour: "HH", Day: "D", Month: "MMM", Year: "YYYY" };
const TOOLTIP_FORMAT = { Hour: "D MMM, HH:00", Day: "D MMM YYYY", Month: "MMM YYYY", Year: "YYYY" };

const formatEnergyValue = (value, decimals = 2) =>
  (Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const formatEcoValue = (value) => {
  const number = Number(value) || 0;
  return Math.abs(number) >= 100
    ? number.toLocaleString("en-US", { maximumFractionDigits: 0 })
    : number.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatTreeValue = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "--";
  if (Math.abs(number) < 1) return number.toFixed(3);
  return number.toLocaleString("en-US", { maximumFractionDigits: 1 });
};

const formatAxisValue = (value) => (value >= 10 ? value.toFixed(0) : value.toFixed(1));

// A bar with rounded top corners.
const barPath = (x, y, width, height, radius) => {
  if (height <= 0 || width <= 0) return "";
  const r = Math.max(0, Math.min(radius, width / 2, height));
  return `M ${x},${y + height} L ${x},${y + r} Q ${x},${y} ${x + r},${y} L ${x + width - r},${y} Q ${x + width},${y} ${x + width},${y + r} L ${x + width},${y + height} Z`;
};

const useWidth = (ref) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const update = () => setWidth(element.clientWidth);
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
};

// Bars for days, months and years; a line over a shaded area for the hours of
// a day. Hovering or tapping a bar shows its value.
const EnergyChart = ({ width, series, metricKey, average, hasAverage, period, referenceDate, unit, xAxisLabel, yAxisLabel }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    setSelectedIndex(null);
  }, [series, period, metricKey, referenceDate]);

  const bars = useMemo(
    () =>
      buildPeriodBuckets(series, period, referenceDate, metricKey).map((item, index) => ({
        ...item,
        index,
        isFuture: isFuture(item.bucket_time, period),
      })),
    [series, period, referenceDate, metricKey]
  );

  if (width <= 0) return null;

  const isLine = period === "Hour";
  const averageShown = (hasAverage ? average : 0) || 0;
  const visible = bars.filter((bar) => !bar.isFuture).map((bar) => bar.value);
  const maxValue = visible.length ? Math.max(...visible, averageShown) : averageShown;
  const chartMax = maxValue <= 1 ? 1 : Math.ceil(maxValue * 1.2 * 10) / 10;

  const left = 45;
  const right = 10;
  const top = 14;
  const bottom = 54;
  const graphWidth = width - left - right;
  const graphHeight = CHART_HEIGHT - top - bottom;
  const barSpace = bars.length ? graphWidth / bars.length : graphWidth;
  const barWidth = bars.length === 1 ? 34 : Math.max(isLine ? 3 : 5, Math.min(isLine ? 8 : 14, barSpace * 0.55));
  const dotRadius = Math.max(3, Math.min(5.5, barSpace * 0.22));
  const getY = (value) => graphHeight - (value / chartMax) * graphHeight;
  const getX = (index) => index * barSpace + barSpace / 2 - barWidth / 2;
  const getLineX = (index) => (bars.length > 1 ? (index / (bars.length - 1)) * graphWidth : graphWidth / 2);

  const linePoints = isLine ? bars.filter((bar) => !bar.isFuture).map((bar) => ({ x: getLineX(bar.index), y: getY(bar.value) })) : [];
  const linePath = linePoints.map((point, index) => `${index ? "L" : "M"} ${point.x},${point.y}`).join(" ");
  const areaPath =
    linePoints.length > 1
      ? `${linePath} L ${linePoints[linePoints.length - 1].x},${graphHeight} L ${linePoints[0].x},${graphHeight} Z`
      : "";

  const ySteps = isLine ? 5 : 4;
  const labelIndexes = labelIndexesFor(bars, period);
  const accent = PURPLE;

  let tooltip = null;
  const active = selectedIndex !== null ? bars[selectedIndex] : null;
  if (active && active.value >= 0 && !active.isFuture) {
    const y = getY(active.value);
    const centerX = isLine ? getLineX(selectedIndex) : getX(selectedIndex) + barWidth / 2;
    const text = `${formatEnergyValue(active.value, 2)} ${unit}`;
    const dateText = moment(active.bucket_time).format(TOOLTIP_FORMAT[period]);
    const bubbleWidth = Math.max(96, Math.max(text.length, dateText.length) * 6 + 34);
    const bubbleX = Math.max(0, Math.min(centerX - bubbleWidth / 2, graphWidth - bubbleWidth));
    const bubbleY = Math.max(y - 76, -2);
    tooltip = (
      <g pointerEvents="none">
        <line
          x1={centerX}
          y1={Math.max(y - dotRadius - 4, 0)}
          x2={centerX}
          y2={Math.max(y - 30, 14)}
          stroke={accent}
          strokeWidth={1}
          strokeDasharray="2,3"
          strokeOpacity={0.4}
        />
        <rect x={bubbleX + 1} y={bubbleY + 3} width={bubbleWidth} height={40} rx={12} fill="#000" opacity={0.08} />
        <rect x={bubbleX} y={bubbleY} width={bubbleWidth} height={40} rx={12} fill="#fff" stroke={accent} strokeWidth={1.4} />
        <circle cx={bubbleX + 16} cy={bubbleY + 20} r={3.5} fill={accent} />
        <text x={bubbleX + 26} y={bubbleY + 17} fontSize={10.5} fontWeight="700" fill="#000">
          {text}
        </text>
        <text x={bubbleX + 26} y={bubbleY + 30} fontSize={8.5} fill={GREY}>
          {dateText}
        </text>
        <polygon
          points={`${centerX - 5},${bubbleY + 40} ${centerX + 5},${bubbleY + 40} ${centerX},${bubbleY + 46}`}
          fill="#fff"
          stroke={accent}
          strokeWidth={1.4}
        />
      </g>
    );
  }

  return (
    <svg
      width={width}
      height={CHART_HEIGHT}
      role="img"
      aria-label={`${yAxisLabel} by ${period.toLowerCase()}`}
      onMouseLeave={() => setSelectedIndex(null)}
    >
      <defs>
        <linearGradient id="hx-emg-bar" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={LIGHT_PURPLE} />
          <stop offset="1" stopColor={PURPLE} />
        </linearGradient>
        <linearGradient id="hx-emg-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={PURPLE} stopOpacity="0.55" />
          <stop offset="1" stopColor={PURPLE} stopOpacity="0.12" />
        </linearGradient>
      </defs>

      <g transform={`translate(${left},${top})`}>
        {Array.from({ length: ySteps + 1 }, (_, index) => {
          const ratio = index / ySteps;
          const y = graphHeight * ratio;
          return (
            <g key={`grid-${index}`}>
              <line
                x1={0}
                y1={y}
                x2={graphWidth}
                y2={y}
                stroke={isLine ? PURPLE : WHITESMOKE}
                strokeWidth={isLine ? 0.6 : 0.7}
                strokeDasharray="4,4"
                strokeOpacity={isLine ? 0.16 : 1}
              />
              <text x={-8} y={y + 3} fontSize={9} fill={GREY} textAnchor="end">
                {formatAxisValue(chartMax * (1 - ratio))}
              </text>
            </g>
          );
        })}

        {hasAverage && average > 0 && average <= chartMax ? (
          <line
            x1={0}
            y1={getY(average)}
            x2={graphWidth}
            y2={getY(average)}
            stroke={accent}
            strokeWidth={1.2}
            strokeDasharray="5,3"
            strokeOpacity={0.75}
          />
        ) : null}

        {isLine && areaPath ? <path d={areaPath} fill="url(#hx-emg-area)" /> : null}
        {isLine && linePath ? (
          <path d={linePath} fill="none" stroke={accent} strokeWidth={2.8} strokeLinecap="square" strokeLinejoin="miter" />
        ) : null}

        {bars.map((bar, index) => {
          const x = getX(index);
          const y = getY(bar.value);
          const height = Math.max(graphHeight - y, bar.value > 0 ? 3 : 0);
          const isActive = index === selectedIndex;
          const centerX = isLine ? getLineX(index) : x + barWidth / 2;
          const select = () => !bar.isFuture && setSelectedIndex(index);
          return (
            <g key={bar.bucket_time}>
              <rect
                x={index * barSpace}
                y={0}
                width={barSpace}
                height={graphHeight}
                fill="transparent"
                onMouseEnter={select}
                onTouchStart={select}
              />
              {isLine
                ? isActive &&
                  !bar.isFuture && (
                    <g pointerEvents="none">
                      <circle cx={centerX} cy={y} r={6} fill={accent} opacity={0.2} />
                      <circle cx={centerX} cy={y} r={dotRadius} fill={accent} stroke="#fff" strokeWidth={2} />
                    </g>
                  )
                : !bar.isFuture && (
                    <path
                      d={barPath(x, graphHeight - height, barWidth, height, 5)}
                      fill={isActive ? PURPLE : "url(#hx-emg-bar)"}
                      opacity={isActive ? 1 : 0.95}
                      pointerEvents="none"
                    />
                  )}
              {labelIndexes.includes(index) ? (
                <text
                  x={centerX}
                  y={graphHeight + 17}
                  fontSize={period === "Day" || period === "Year" ? 9 : 8}
                  fill={bar.isFuture ? LIGHTGRAY : isActive ? accent : GREY}
                  fontWeight={isActive ? "600" : "400"}
                  textAnchor="middle"
                  pointerEvents="none"
                >
                  {moment(bar.bucket_time).format(AXIS_FORMAT[period])}
                </text>
              ) : null}
            </g>
          );
        })}

        <line
          x1={0}
          y1={graphHeight}
          x2={graphWidth}
          y2={graphHeight}
          stroke={isLine ? PURPLE : GREY}
          strokeWidth={0.8}
          strokeOpacity={isLine ? 0.35 : 1}
        />
        <line x1={0} y1={0} x2={0} y2={graphHeight} stroke={isLine ? PURPLE : GREY} strokeWidth={0.8} strokeOpacity={isLine ? 0.35 : 1} />

        <text x={-(graphHeight / 2)} y={-30} transform="rotate(-90)" fontSize={10} fill="#000" fontWeight="500" textAnchor="middle">
          {yAxisLabel}
        </text>
        <text x={graphWidth / 2} y={graphHeight + 43} fontSize={10} fill="#000" fontWeight="500" textAnchor="middle">
          {xAxisLabel}
        </text>

        {tooltip}
      </g>
    </svg>
  );
};

const EnvironmentItem = ({ icon, value, label, color }) => (
  <div className="hx-emg-eco-item">
    <span className="hx-emg-eco-icon" style={{ background: `${color}1A` }} aria-hidden="true">
      <MdiIcon path={icon} size={22} color={color} />
    </span>
    <span className="hx-emg-eco-value">{value}</span>
    <span className="hx-emg-eco-label">{label}</span>
  </div>
);

function EnergyMeterGraph() {
  const location = useLocation();
  const toast = useToggleTop();
  const device = location.state?.data;
  const metric = METRICS[location.state?.metric] ? location.state.metric : "consumed";
  const config = METRICS[metric];
  const deviceId = device?.deviceId;
  const [period, setPeriod] = useState("Day");
  const [selectedDate, setSelectedDate] = useState(() => moment());
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const chartRef = useRef(null);
  const chartWidth = useWidth(chartRef);

  useEffect(() => {
    if (!deviceId) return undefined;
    let current = true;
    const { from, to } = rangeFor(period, selectedDate);
    setLoading(true);
    UserService.getEnergyGraph(deviceId, period.toLowerCase(), from, to)
      .then((response) => {
        if (!current) return;
        setSummary(response?.summary || null);
        setChartData(response?.chart_data || null);
      })
      .catch(() => {
        if (!current) return;
        setSummary(null);
        setChartData(null);
        toast("Couldn't load the graph. Please try again.", "red");
      })
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
    // toast is a new function on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, period, selectedDate]);

  const total = useMemo(() => {
    if (!config.hasTotal) return null;
    return Number((metric === "generated" ? summary?.total_energy_generated_kwh : summary?.total_energy_consumed_kwh) ?? 0);
  }, [config, metric, summary]);

  // The API returns the average under a key for the period it was asked for.
  const average = useMemo(() => {
    if (!config.hasAverage) return null;
    const prefix = metric === "generated" ? "avg_generation_per" : "avg_usage_per";
    const value = ["hour", "day", "month", "year"].map((unit) => summary?.[`${prefix}_${unit}_kwh`]).find((item) => item !== undefined && item !== null);
    return value !== undefined ? Number(value) : 0;
  }, [config, metric, summary]);

  const environment = useMemo(() => {
    if (!config.hasEnvironment) return null;
    if (metric === "generated") {
      const savings = summary?.eco_impact?.generation_savings;
      return {
        co2: Number(savings?.total_co2_reduced_kg ?? 0),
        coal: Number(savings?.total_coal_saved_kg ?? 0),
        trees: savings?.total_trees_saved !== undefined ? Number(savings.total_trees_saved) : null,
      };
    }
    const footprint = summary?.eco_impact?.consumption_footprint;
    return {
      co2: Number(footprint?.total_co2_produced_kg ?? 0),
      coal: Number(footprint?.total_coal_burned_kg ?? 0),
      trees: footprint?.total_trees_destroyed !== undefined ? Number(footprint.total_trees_destroyed) : null,
    };
  }, [config, metric, summary]);

  if (!device) return <Redirect to="/dashboard" />;

  const now = moment();
  const nextDisabled =
    period === "Hour"
      ? selectedDate.isSame(now, "day")
      : period === "Day"
      ? selectedDate.isSame(now, "month")
      : period === "Month"
      ? selectedDate.isSame(now, "year")
      : true;
  const dateLabel = period === "Hour" ? selectedDate.format("DD MMM YYYY") : period === "Day" ? selectedDate.format("MMMM YYYY") : selectedDate.format("YYYY");
  const step = { Hour: "day", Day: "month", Month: "year" }[period];

  const changeDate = (direction) => {
    if (direction > 0 && nextDisabled) return;
    if (step) setSelectedDate(selectedDate.clone().add(direction, step));
  };

  const changePeriod = (value) => {
    setPeriod(value);
    setSelectedDate(moment());
  };

  const generated = metric === "generated";

  return (
    <Main header="stack" title="">
      <div className="hx-em-page">
        <div className="hx-emg-periods" role="tablist" aria-label="Period">
          {PERIODS.map((item) => (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={period === item}
              className={`hx-emg-period ${period === item ? "is-active" : ""}`}
              onClick={() => changePeriod(item)}
            >
              {item}
            </button>
          ))}
        </div>

        {period !== "Year" ? (
          <div className="hx-emg-datenav">
            <button type="button" className="hx-emg-arrow" aria-label={`Previous ${step}`} onClick={() => changeDate(-1)}>
              <MdiIcon path={mdiChevronLeft} size={19} />
            </button>
            <span className="hx-emg-date">{dateLabel}</span>
            <button type="button" className="hx-emg-arrow" aria-label={`Next ${step}`} disabled={nextDisabled} onClick={() => changeDate(1)}>
              <MdiIcon path={mdiChevronRight} size={19} />
            </button>
          </div>
        ) : null}

        {config.hasTotal || config.hasAverage ? (
          <div className={`hx-emg-summary ${config.hasTotal ? "" : "is-single"}`}>
            <span className="hx-emg-summary-icon" aria-hidden="true">
              <MdiIcon path={config.icon} size={26} color="#fff" />
            </span>
            {config.hasTotal ? (
              <>
                <div className="hx-emg-summary-col">
                  <div className="hx-emg-summary-label">{config.totalLabel}</div>
                  <div className="hx-emg-summary-value is-accent">
                    {formatEnergyValue(total, 2)}
                    <small> {config.unit}</small>
                  </div>
                </div>
                <span className="hx-emg-summary-divider" />
              </>
            ) : null}
            {config.hasAverage ? (
              <div className="hx-emg-summary-col">
                <div className="hx-emg-summary-label">{config.averageLabel}</div>
                <div className={`hx-emg-summary-value ${config.hasTotal ? "" : "is-accent"}`}>
                  {formatEnergyValue(average, 2)}
                  <small> {config.unit}</small>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="hx-emg-chart" ref={chartRef}>
          {loading ? (
            <div className="hx-emg-loading">
              <span className="hx-em-spinner" aria-label="Loading" />
            </div>
          ) : (
            <EnergyChart
              width={chartWidth}
              series={chartData?.series}
              metricKey={config.key}
              average={average}
              hasAverage={Boolean(config.hasAverage)}
              period={period}
              referenceDate={selectedDate}
              unit={config.unit}
              xAxisLabel={chartData?.x_axis_label || "Periods (day)"}
              yAxisLabel={config.yAxisLabel}
            />
          )}
        </div>

        <div className="hx-emg-legend">
          <span className="hx-emg-pill">
            <span className="hx-emg-pill-bar" />
            {config.legendTitle}
            <small>({config.unit})</small>
          </span>
          {config.hasAverage ? (
            <span className="hx-emg-pill">
              <span className="hx-emg-pill-dashes" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
              Average
              <small>({config.unit})</small>
            </span>
          ) : null}
        </div>

        {environment ? (
          <div className="hx-emg-eco">
            <EnvironmentItem
              icon={mdiCloudOutline}
              color="#0a62f1"
              value={`${formatEcoValue(environment.co2)} kg`}
              label={generated ? "CO₂ reduced" : "CO₂ produced"}
            />
            <span className="hx-emg-eco-divider" />
            <EnvironmentItem
              icon={mdiFire}
              color={GREY}
              value={`${formatEcoValue(environment.coal)} kg`}
              label={generated ? "Coal saved" : "Coal burned"}
            />
            <span className="hx-emg-eco-divider" />
            <EnvironmentItem
              icon={mdiPineTree}
              color="#4caf50"
              value={environment.trees !== null ? `${formatTreeValue(environment.trees)} Trees` : "--"}
              label={generated ? "Trees Saved" : "Trees Affected"}
            />
          </div>
        ) : null}
      </div>
    </Main>
  );
}

export default EnergyMeterGraph;
